/**
 * One form: its versions, where each came from, and how to start answering it.
 *
 * The version's origin is shown on every row because it is the difference between a
 * structure a person checked and one a model proposed and nobody read. A fill against an
 * unreviewed extraction is allowed — waiting for review would stall real work — but it is
 * never silent about it.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { get, post } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type {
  CollectionSummary,
  FillSummary,
  Job,
  JobList,
  TemplateSummary,
  TemplateVersionSummary,
} from "../lib/types";
import { Shell } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Chip, Empty, Failed, Panel } from "../components/ui";
import { ExtractSource } from "../components/ExtractSource";
import { ago } from "../lib/format";

const ORIGIN_TONE = {
  extracted: "warn",
  edited: "info",
  authored: "mut",
} as const;

export function TemplateDetail() {
  const { id = "" } = useParams();
  const client = useQueryClient();
  const navigate = useNavigate();
  const can = usePermission();
  const canWrite = can("documents:write");

  // Failures the operator has read and put away, by job id. Persisted, because a banner
  // that returns on every reload is one the reader stops seeing — and the next real
  // failure arrives looking exactly like the one they already dismissed.
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("alt.extract.dismissed") ?? "[]") as string[];
    } catch {
      return [];
    }
  });

  function dismiss(jobId: string) {
    // Bounded: the last 50 are plenty to stop a banner reappearing, and this must never
    // grow without limit in someone's browser.
    const next = [jobId, ...dismissed.filter((one) => one !== jobId)].slice(0, 50);
    setDismissed(next);
    try {
      localStorage.setItem("alt.extract.dismissed", JSON.stringify(next));
    } catch {
      /* a browser refusing storage is not a reason to fail the click */
    }
  }
  const [collection, setCollection] = useState("");

  const template = useQuery({
    queryKey: ["templates", id],
    queryFn: () => get<TemplateSummary>(`/v1/templates/${id}`),
  });
  const versions = useQuery({
    queryKey: ["templates", id, "versions"],
    queryFn: () => get<{ versions: TemplateVersionSummary[] }>(`/v1/templates/${id}/versions`),
  });
  // Extraction runs in the queue, so its outcome lands minutes after the click. Without
  // this the page looked idle whether the job was running, finished, or dead — and the
  // only place the reason existed was the Jobs screen.
  const extraction = useQuery({
    queryKey: ["jobs", "template_extract"],
    queryFn: () => get<JobList>("/v1/jobs?kind=template_extract&limit=20"),
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      return jobs.some((job) => job.state === "queued" || job.state === "running") ? 3000 : false;
    },
  });
  const collections = useQuery({
    queryKey: ["collections", "for-fill"],
    queryFn: () => get<{ collections: CollectionSummary[] }>("/v1/collections"),
  });

  const extract = useMutation({
    mutationFn: (documentId: string) =>
      post<{ job_id: string }>(`/v1/templates/${id}/extract`, { document_id: documentId }),
    onSuccess: () => {
      // The job is queued, not finished. Both the versions list and the extraction status
      // below are invalidated so the page starts following it immediately.
      void client.invalidateQueries({ queryKey: ["templates", id] });
      void client.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  const startFill = useMutation({
    mutationFn: () =>
      post<FillSummary>("/v1/fills", { template_id: id, collection_id: collection }),
    onSuccess: (fill) => navigate(`/fills/${fill.id}`),
  });

  // A queued or running extraction, and the most recent failed one — the two states the
  // page must not stay silent about.
  const jobs = extraction.data?.jobs ?? [];
  const running = jobs.find((job: Job) => job.state === "queued" || job.state === "running");
  const failed = jobs
    .filter((job: Job) => job.state === "failed" || job.state === "dead")
    .find((job: Job) => !dismissed.includes(job.id));
  const rows = versions.data?.versions ?? [];
  // Newest first, so the head of the list is the version a new fill would pin.
  const latest = rows.at(0);
  const hasStructure = latest !== undefined;

  return (
    <Shell
      fill
      crumb={
        <>
          <a href="/templates" className="text-dim hover:text-gold">
            templates
          </a>
          <span className="text-faint"> / </span>
          <span className="text-text">{template.data?.name ?? id}</span>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-px">
        <Panel
          title="versions"
          aside={
            latest ? (
              <Chip tone={ORIGIN_TONE[latest.origin as keyof typeof ORIGIN_TONE] ?? "mut"}>
                v{latest.version} · {latest.origin}
              </Chip>
            ) : (
              <Chip tone="warn">no structure yet</Chip>
            )
          }
          actions={
            canWrite && (
              <ExtractSource
                disabled={extract.isPending || running !== undefined}
                onExtract={(documentId) => extract.mutate(documentId)}
              />
            )
          }
        >
          {running !== undefined && (
            <p className="border-b border-hair bg-gold-wash px-4 py-2.5 text-[12.5px]">
              Reading the form — this runs in the queue and lands as a new version below.
            </p>
          )}
          {/* Dated, and only while nothing newer has succeeded. An error with no time on
              it reads as if it just happened — this one sat on the page for hours after the
              cause was fixed, and looked identical to a live failure. */}
          {failed !== undefined && running === undefined && rows.length === 0 && (
            <div className="flex items-start gap-3 border-b border-hair bg-crit-wash px-4 py-2.5 text-[12.5px]">
              <p className="min-w-0">
                <span className="font-mono text-[10px] tracking-[0.18em] text-crit uppercase">
                  // last attempt failed{failed.updated_at ? ` ${ago(failed.updated_at)}` : ""}{" "}
                </span>
                {failed.last_error ?? "no reason recorded"}
              </p>
              <button
                type="button"
                aria-label="dismiss this failure"
                onClick={() => dismiss(failed.id)}
                className="ms-auto shrink-0 font-mono text-[11px] tracking-[0.14em] text-dim uppercase hover:text-gold"
              >
                dismiss
              </button>
            </div>
          )}
          {versions.isError ? (
            <Failed error={versions.error} what="versions" />
          ) : !versions.isPending && rows.length === 0 ? (
            <Empty
              title="no structure yet"
              hint="Pick a parsed scan of the blank form and extract its structure. A model proposes the sections and rows; you correct them before anyone relies on it."
            />
          ) : (
            <DataTable
              table="template-versions"
              loading={versions.isPending}
              rows={rows}
              rowKey={(row) => row.id}
              columns={[
                {
                  key: "version",
                  header: "version",
                  width: 90,
                  cellClass: "tnum font-mono",
                  render: (row) => `v${row.version}`,
                },
                {
                  key: "origin",
                  header: "origin",
                  width: 130,
                  render: (row) => (
                    <Chip tone={ORIGIN_TONE[row.origin as keyof typeof ORIGIN_TONE] ?? "mut"}>
                      {row.origin}
                    </Chip>
                  ),
                },
                {
                  key: "shape",
                  header: "shape",
                  width: 220,
                  cellClass: "text-dim",
                  render: (row) =>
                    `${row.structure.sections.length} sections · ${row.structure.sections.reduce(
                      (total, section) => total + section.rows.length,
                      0,
                    )} rows`,
                },
                {
                  key: "blanks",
                  header: "declared blanks",
                  width: 160,
                  cellClass: "text-dim",
                  render: (row) =>
                    (row.structure.variables ?? []).length === 0
                      ? "—"
                      : (row.structure.variables ?? []).map((variable) => variable.key).join(", "),
                },
                {
                  key: "created",
                  header: "created",
                  width: 130,
                  cellClass: "text-dim",
                  render: (row) => (row.created_at ? ago(row.created_at) : "—"),
                },
                {
                  // The form itself, blank. Same layout as a filled export of it — one
                  // renderer — so what an operator hands out and what comes back answered
                  // are the same document.
                  key: "document",
                  header: "blank form",
                  width: 170,
                  render: (row) => (
                    <span className="flex items-center gap-2 font-mono text-[11px] uppercase">
                      {(["pdf", "docx", "xlsx"] as const).map((format) => (
                        <a
                          key={format}
                          href={`/v1/templates/${id}/versions/${row.id}/document?format=${format}`}
                          target="_blank"
                          rel="noreferrer"
                          className="tracking-[0.12em] text-dim hover:text-gold"
                        >
                          {format}
                        </a>
                      ))}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Panel>

        {hasStructure && canWrite && (
          <Panel
            title="answer this form"
            actions={
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (collection !== "") startFill.mutate();
                }}
                className="flex items-center gap-2"
              >
                <select
                  aria-label="collection to answer from"
                  value={collection}
                  onChange={(event) => setCollection(event.target.value)}
                  className="border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
                >
                  <option value="">from collection…</option>
                  {(collections.data?.collections ?? []).map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name} ({entry.document_count})
                    </option>
                  ))}
                </select>
                <Button variant="primary" type="submit" disabled={collection === "" || startFill.isPending}>
                  start fill
                </Button>
              </form>
            }
          >
            <p className="px-4 py-3 text-[12.5px] text-dim">
              A fill pins the version it starts against, so editing this form afterwards does not
              change what an open fill is answering.
            </p>
          </Panel>
        )}
      </div>
    </Shell>
  );
}
